import { Injectable } from '@angular/core';
import { ConfigService } from '../core/config/config.service';
import { UsertypeService } from '../core/usertype/usertype.service';
import { LinkCondition } from '../model/link.model';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { Claim, Profile, ProfileService } from '../core/profile/profile.service';
import { TranslocoService } from '@jsverse/transloco';
import {
  DefaultCountryPerLangType,
  lookupCountry,
  DefaultCountryPerLang,
} from '../core/util/lookup-default-country.util';
import { HeaderVisibilityService } from '../core/header-visibility/header-visibility.service';
import { OtherPersonContextService } from '../core/other-person-context/other-person-context.service';
import { CoreModule, LoggerService } from '../core';
import { UserTypes } from '../../enums/user-type.enum';
import { WhiteLabelNames } from '../../enums/white-label.enum';
import { Config } from '../model/config.model';
import { setLangCode } from '../../utils/set-lang-code';
import { mpiIdQueryParams, withMpiId } from '../../utils/mpi-id-query';

const hasAny = (req: string[], ut: string[]) => ut.some(u => req.indexOf(u) > -1);
const has = (req: string[], ut: string[]) => req.every(r => ut.indexOf(r) > -1);
const hasTrueClaim = (c: string, req: Claim[]) =>
  req && req.find(x => x.claimType === c)?.claimValue?.toLocaleLowerCase() === 'true';
const formatLangCode = (langCode: string): string => {
  if (langCode.length >= 5) {
    return langCode.substring(0, 5);
  }
  if (langCode.length >= 2) {
    return langCode.substring(0, 2);
  }
  return langCode;
};
const langCodeQueryStr = (link: string, lang: string, countries: DefaultCountryPerLangType) =>
  `${link}?langCode=${formatLangCode(`${lang}-${lookupCountry(lang, countries).toUpperCase()}`)}`;

const countryPerLang: DefaultCountryPerLangType = {
  ...DefaultCountryPerLang,
  en: 'gb',
  sv: 'fi',
  fi: 'fi',
};

const PLASMA_WEB = 'plasma-web';
const PLASMA_ACCOUNTS = 'sts-accounts';

/**
 * User types that must not see the "Home" link.
 *
 * Home is the one entry gated by exclusion rather than by an allowlist, so any
 * newly introduced user type inherits it by default — add such types here to opt
 * them out. This is the only place that needs to change.
 */
export const USER_TYPES_WITHOUT_HOME_LINK: readonly UserTypes[] = [
  UserTypes.RESULT_VIEWER,
];

const isMenuLinkNotHidden = (config: Config): boolean => {
  if ([PLASMA_WEB, 'plasmaui-code-pkce'].includes(config.applicationId)) {
    return false;
  }

  const isEnableCheckResults = config.EnableCheckResults;
  if (!isEnableCheckResults) {
    return true;
  }
  return false;
};

@Injectable({
  providedIn: CoreModule,
})
export class SynlabHeaderService {
  links$ = combineLatest([
    this.usertypeService.usertypes$,
    this.configService.config$,
    this.profileService.profile$,
    this.translocoService.langChanges$,
    this.otherPersonContext.mpiId$,
  ]).pipe(
    map(([ut, c, p, lang, mpiId]) =>
      this.linkCondition
        .filter(lc => lc.condition(ut, c, p))
        .map(lc => ({
          ...lc,
          label: typeof lc.label === 'function' ? lc.label(c) : lc.label,
          url: lc.urlGenerator ? lc.urlGenerator(c, lang, mpiId) : lc.url,
          queryParams: lc.queryParamsGenerator ? lc.queryParamsGenerator(mpiId) : lc.queryParams,
          isDisabled: lc.isDisabled ? lc.isDisabled(p.claims ?? []) : false,
        })),
    ),
  );

  user$ = this.profileService.profile$;

  myAccountLink$ = combineLatest([
    this.configService.config$,
    this.translocoService.langChanges$,
  ]).pipe(map(([c, lang]) => c.baseProfileUrl + langCodeQueryStr('', lang, countryPerLang)));

  brandLogoLink$ = combineLatest([
    this.configService.config$,
    this.translocoService.langChanges$,
  ]).pipe(map(([c, lang]) => c.baseBookingUrl + langCodeQueryStr('', lang, countryPerLang)));

  visible$ = this.headerVisibilityService.visible$;

  config$ = this.configService.config$;

  constructor(
    private usertypeService: UsertypeService,
    private configService: ConfigService,
    private profileService: ProfileService,
    private headerVisibilityService: HeaderVisibilityService,
    private translocoService: TranslocoService,
    private logr: LoggerService,
    private otherPersonContext: OtherPersonContextService,
  ) {}

  private linkCondition: LinkCondition[] = [
    {
      label: () => 'homeLabel',
      icon: '/assets/synlab-header/panel.svg',
      urlGenerator: (c, lang) => {
        return c.baseAdminUrl;
      },
      // Spread because `hasAny` takes a mutable `string[]`; keeping the constant
      // `readonly` is worth more than widening a helper shared by every link.
      condition: ut =>
        !has(['synlab_access_user'], ut) && !hasAny([...USER_TYPES_WITHOUT_HOME_LINK], ut),
    },
    {
      label: c => {
        if (c.WhiteLabelName === 'uk-dfp') return 'webshopLabel';
        return 'bigCommerceLabel';
      },
      icon: '/assets/synlab-header/panel.svg',
      urlGenerator: (c, lang) => {
        if (isMenuLinkNotHidden(c)) return '';
        if (
          c.WhiteLabelName === WhiteLabelNames.CZECH_REPUBLIC ||
          c.WhiteLabelName === WhiteLabelNames.BELGIUM
        )
          return '';

        const linksPerLang: Record<string, string> = {};
        c.baseBigcommerceUrl
          ?.split(',')
          .map(u => u.split('|'))
          .forEach(l => {
            const [locale, url] = l;
            linksPerLang[locale] = url;
          });
        if (lang in linksPerLang) {
          return linksPerLang[lang];
        }
        return linksPerLang['**'] ?? c.baseBigcommerceUrl;
      },
      condition: ut => has(['synlab_access_user'], ut),
    },
    {
      label: c => {
        if (c.WhiteLabelName === 'uk-dfp') return 'ordersLabel';
        return 'ordersBookingLabel';
      },
      icon: '/assets/synlab-header/panel.svg',
      urlGenerator: (c, inputLang) => {
        const lang = formatLangCode(
          `${inputLang}-${(lookupCountry(inputLang, countryPerLang) || '').toUpperCase()}`,
        );
        if (isMenuLinkNotHidden(c)) return false;
        if (
          c.WhiteLabelName === WhiteLabelNames.CZECH_REPUBLIC ||
          c.WhiteLabelName === WhiteLabelNames.BELGIUM
        )
          return false;

        const isFITenant = c.WhiteLabelEnabled && c.WhiteLabelName == WhiteLabelNames.FINLAND;
        const isHUTenant = c.WhiteLabelEnabled && c.WhiteLabelName == WhiteLabelNames.HUNGARY;
        const isUKDfpTenant = c.WhiteLabelEnabled && c.WhiteLabelName == WhiteLabelNames.UK_DFP;
        const isITTenant = c.WhiteLabelEnabled && c.WhiteLabelName == WhiteLabelNames.ITALY;
        const isGlobal = c.WhiteLabelEnabled && c.WhiteLabelName == WhiteLabelNames.GLOBAL;
        if (isHUTenant || isFITenant || isUKDfpTenant || isITTenant || isGlobal) {
          return setLangCode(c.baseBookingUrl, lang);
        }

        const isHungary = c.WhiteLabelName == WhiteLabelNames.HUNGARY;
        if (isHungary && c.WhiteLabelEnabled) {
          return setLangCode(c.baseBookingUrl, lang);
        }

        let linksPerLang = {};
        c.eeBaseBookingUrl
          .split(',')
          .map(u => u.split('|'))
          .forEach(l => {
            const [locale, url] = l;
            linksPerLang[locale] = setLangCode(url, lang);
          });
        if (lang in linksPerLang) {
          return linksPerLang[lang];
        }
        return linksPerLang['*'] ?? setLangCode(c.eeBaseBookingUrl, lang);
      },
      condition: (ut, c) => has(['synlab_access_user'], ut) && !isMenuLinkNotHidden(c),
    },
    {
      label: 'resultsCertificateLabel',
      icon: '/assets/synlab-header/results.svg',
      urlGenerator: (c, lang, mpiId) => {
        return withMpiId(
          c.ResultsAndCertificateCustomLink ||
            `${c.basePlasmaUrl}/results/category${langCodeQueryStr('', lang, countryPerLang)}`,
          mpiId,
        );
      },
      condition: (ut, c) => {
        if (isMenuLinkNotHidden(c)) return false;

        return (
          has(['synlab_access_user'], ut) &&
          c.FullCatalogueViewEnabled === true &&
          c.applicationId === PLASMA_ACCOUNTS
        );
      },
      isDisabled: c => {
        const isActive =
          c.find(claim => claim.claimType === 'active')?.claimValue?.toLowerCase() === 'true';
        const isEmailVerified =
          c.find(claim => claim.claimType === 'email_verified')?.claimValue?.toLowerCase() ===
          'true';
        return !isActive && !isEmailVerified;
      },
    },
    {
      label: 'resultsCertificateLabel',
      icon: '/assets/synlab-header/results.svg',
      urlGenerator: (c, lang, mpiId) => withMpiId(c.ResultsAndCertificateCustomLink, mpiId),
      condition: (ut, c) => {
        if (isMenuLinkNotHidden(c)) {
          return false;
        }

        return (
          has(['synlab_access_user'], ut) &&
          c.FullCatalogueViewEnabled === true &&
          c.applicationId === PLASMA_WEB &&
          !!c.ResultsAndCertificateCustomLink
        );
      },
      isDisabled: c => {
        const isActive =
          c.find(claim => claim.claimType === 'active')?.claimValue?.toLowerCase() === 'true';
        const isEmailVerified =
          c.find(claim => claim.claimType === 'email_verified')?.claimValue?.toLowerCase() ===
          'true';
        return !isActive && !isEmailVerified;
      },
    },
    {
      label: 'resultsCertificateLabel',
      icon: '/assets/synlab-header/results.svg',
      route: '/results',
      queryParamsGenerator: mpiIdQueryParams,
      condition: (ut, c) => {
        if (isMenuLinkNotHidden(c)) {
          return false;
        }

        return (
          has(['synlab_access_user'], ut) &&
          c.FullCatalogueViewEnabled === true &&
          c.applicationId === PLASMA_WEB &&
          !c.ResultsAndCertificateCustomLink
        );
      },
      isDisabled: c => {
        const isActive =
          c.find(claim => claim.claimType === 'active')?.claimValue?.toLowerCase() === 'true';
        const isEmailVerified =
          c.find(claim => claim.claimType === 'email_verified')?.claimValue?.toLowerCase() ===
          'true';
        return !isActive && !isEmailVerified;
      },
    },
    {
      label: 'checkResultLabel',
      icon: '/assets/synlab-header/results.svg',
      urlGenerator: (c, lang) =>
        `${c.basePlasmaUrl}/results/category${langCodeQueryStr('', lang, countryPerLang)}`,
      condition: (localUserTypes: string[], config: Config, profile: Profile) => {
        if (isMenuLinkNotHidden(config)) {
          return false;
        }

        return (
          hasAny(['ee_admin', 'pt_admin'], localUserTypes) &&
          config.FullCatalogueViewEnabled === true &&
          hasTrueClaim('allow_delete_user', profile.claims) &&
          config.applicationId === PLASMA_ACCOUNTS
        );
      },
    },
    {
      label: 'checkResultLabel',
      icon: '/assets/synlab-header/results.svg',
      route: '/results/category',
      queryParamsGenerator: mpiIdQueryParams,
      condition: (localUserTypes: string[], config: Config, profile: Profile) => {
        if (isMenuLinkNotHidden(config)) {
          return false;
        }

        return (
          hasAny(['ee_admin', 'pt_admin'], localUserTypes) &&
          config.FullCatalogueViewEnabled === true &&
          hasTrueClaim('allow_delete_user', profile.claims) &&
          config.applicationId === PLASMA_WEB
        );
      },
    },
    {
      label: 'preventionWellness',
      icon: '/assets/synlab-header/heart.svg',
      urlGenerator: (c, lang) =>
        `${c.basePlasmaUrl}/prevention-wellness${langCodeQueryStr('', lang, countryPerLang)}`,
      condition: (ut, c, p) =>
        hasAny(['synlab_access_user'], ut) &&
        c.EnablePreventionWellness === true &&
        !hasTrueClaim('anonymous_user', p.claims) &&
        c.applicationId === PLASMA_ACCOUNTS,
    },
    {
      label: 'preventionWellness',
      icon: '/assets/synlab-header/heart.svg',
      route: '/prevention-wellness',
      condition: (ut, c, p) =>
        hasAny(['synlab_access_user'], ut) &&
        c.EnablePreventionWellness === true &&
        !hasTrueClaim('anonymous_user', p.claims) &&
        c.applicationId === PLASMA_WEB,
    },
    {
      label: 'adminLabel',
      icon: '/assets/synlab-header/people.svg',
      urlGenerator: (c, lang) => langCodeQueryStr(c.baseAdminUrl, lang, countryPerLang),
      condition: ut =>
        hasAny(
          [
            'uk_shfy_admin',
            'uk_dfp_admin',
            'ee_admin',
            'notification_user',
            'superadmin',
            'pt_admin',
          ],
          ut,
        ),
    },
    {
      label: 'bookingAdminLabel',
      icon: '/assets/synlab-header/people.svg',
      urlGenerator: (c, lang) => langCodeQueryStr(c.baseBookingAdminUrl, lang, countryPerLang),
      condition: (ut, c) => {
        const tenantName: WhiteLabelNames = c.WhiteLabelName as unknown as WhiteLabelNames;
        return (
          hasAny(
            [
              UserTypes.BOOKING_ADMIN_BCP_COORDINATOR,
              UserTypes.BOOKING_ADMIN_BCP_CLERK,
              UserTypes.BOOKING_ADMIN,
            ],
            ut,
          ) && [WhiteLabelNames.HUNGARY, WhiteLabelNames.FINLAND].includes(tenantName)
        );
      },
    },
    {
      label: 'webreqLabel',
      icon: '/assets/synlab-header/people.svg',
      urlGenerator: (c, lang) => langCodeQueryStr(c.baseWebreqUrl, lang, countryPerLang),
      condition: ut => hasAny(['bcp', 'webreq_user', 'webreq_admin', 'webreq_finance', 'laboratory_order_viewer'], ut),
    },
    {
      label: 'webreqLabelAdmin',
      icon: '/assets/synlab-header/people.svg',
      urlGenerator: (c, lang) => langCodeQueryStr(c.baseWebreqAdminUrl, lang, countryPerLang),
      condition: ut => hasAny(['webreq_superadmin'], ut),
    },
    {
      label: 'labShopLabel',
      urlGenerator: (c, lang) => langCodeQueryStr(c.baseBookingUrl, lang, countryPerLang),
      condition: (ut, c) => {
        const isESTenant = c.WhiteLabelEnabled && c.WhiteLabelName === WhiteLabelNames.SPAIN;
        this.logr.log({
          WhiteLabelEnabled: c.WhiteLabelEnabled,
          WhiteLabelName: c.WhiteLabelName,
          isSpain: c.WhiteLabelName === WhiteLabelNames.SPAIN,
          hasAny: hasAny(['webshop_admin', 'webshop_user'], ut),
        });
        if (isESTenant && hasAny(['webshop_admin', 'webshop_user'], ut)) {
          return true;
        }
        return false;
      },
    },
    {
      label: 'medioLabel',
      urlGenerator: (c, lang) => langCodeQueryStr('https://medicall.cc', lang, countryPerLang),
      condition: (ut, c) => {
        const isEsTcTenant =
          c.WhiteLabelEnabled && c.WhiteLabelName === WhiteLabelNames.SPAIN_TELECOUNSELING;
        if (isEsTcTenant && hasAny(['pt_admin', 'synlab_access_user', 'expert'], ut)) {
          return true;
        }
        return false;
      },
    },
  ];
}
